import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  Stack,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Euro,
  Save,
  ArrowBack,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../api';

interface Settings {
  _id: string;
  consultingFee: number;
  referredFee: number;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

export default function SettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [consultingFee, setConsultingFee] = useState<number>(150);
  const [referredFee, setReferredFee] = useState<number>(50);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/settings');
      const settingsData = response.data.data;
      setSettings(settingsData);
      setConsultingFee(settingsData.consultingFee);
      setReferredFee(settingsData.referredFee);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Validation
      if (consultingFee <= 0) {
        setError('Taxa de consultoria deve ser maior que zero');
        return;
      }

      if (referredFee <= 0) {
        setError('Taxa de indicação deve ser maior que zero');
        return;
      }

      if (referredFee >= consultingFee) {
        setError('Taxa de indicação deve ser menor que a taxa de consultoria');
        return;
      }

      await api.put('/settings', {
        consultingFee,
        referredFee
      });

      setSuccess('Configurações salvas com sucesso!');
      fetchSettings(); // Refresh data

    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const calculateClientFee = () => consultingFee - referredFee;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: (theme) => theme.palette.grey[100], py: 3 }}>
      <Container maxWidth="md">
        <Stack spacing={3}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate(-1)}
              variant="outlined"
            >
              Voltar
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <SettingsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                Configurações do Sistema
              </Typography>
            </Box>
          </Box>

          {/* Settings Form */}
          <Paper elevation={2} sx={{ p: 4 }}>
            <Stack spacing={4}>
              <Typography variant="h6" sx={{ color: '#2c3e50', fontWeight: 600, mb: 2 }}>
                Configurações Financeiras
              </Typography>

              {/* Consulting Fee */}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Taxa de Consultoria
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={consultingFee}
                  onChange={(e) => setConsultingFee(Number(e.target.value))}
                  slotProps={{
                    input: {
                      startAdornment: <InputAdornment position="start"><Euro /></InputAdornment>,
                      inputProps: { min: 0, step: 0.01 }
                    }
                  }}
                  helperText="Valor cobrado aos clientes diretos"
                  variant="outlined"
                />
              </Box>

              {/* Referred Fee */}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Taxa de Indicação para Parceiros
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={referredFee}
                  onChange={(e) => setReferredFee(Number(e.target.value))}
                  slotProps={{
                    input: {
                      startAdornment: <InputAdornment position="start"><Euro /></InputAdornment>,
                      inputProps: { min: 0, step: 0.01 }
                    }
                  }}
                  helperText="Valor pago aos parceiros por indicação"
                  variant="outlined"
                />
              </Box>

              {/* Fee Calculation Preview */}
              <Box sx={{ 
                p: 3, 
                bgcolor: 'info.lighter', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'info.main'
              }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'info.main' }}>
                  Resumo dos Valores
                </Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>Taxa de Consultoria (clientes diretos):</Typography>
                    <Typography sx={{ fontWeight: 600 }}>€{consultingFee.toFixed(2)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>Taxa de Indicação (parceiros):</Typography>
                    <Typography sx={{ fontWeight: 600 }}>€{referredFee.toFixed(2)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid', borderColor: 'info.main' }}>
                    <Typography sx={{ fontWeight: 600 }}>Taxa cobrada de clientes indicados:</Typography>
                    <Typography sx={{ fontWeight: 600, color: 'success.main' }}>
                      €{calculateClientFee().toFixed(2)}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              {/* Alerts */}
              {error && (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ borderRadius: 2 }}>
                  {success}
                </Alert>
              )}

              {/* Save Button */}
              <Button
                variant="contained"
                size="large"
                startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                onClick={handleSave}
                disabled={saving}
                sx={{
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none'
                }}
              >
                {saving ? 'Salvando...' : 'Salvar Configurações'}
              </Button>

              {/* Last Updated Info */}
              {settings && (
                <Box sx={{ pt: 2, borderTop: '1px solid', borderColor: 'grey.300' }}>
                  <Typography variant="caption" color="text.secondary">
                    Última atualização: {new Date(settings.updatedAt).toLocaleString('pt-BR')} por {settings.updatedBy}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
} 